import { useAtomValue } from "jotai";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ThemeProvider as StyledThemeProvider,
  styled,
} from "styled-components";
import "./App.css";
import AppHeader from "./components/AppHeader/AppHeader";

import { panelIsOpenState } from "./store";

import {
  FluentProvider,
  webDarkTheme,
  webLightTheme,
} from "@fluentui/react-components";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { GlsEditor } from "components/GlsEditor/GlsEditor";
import GuardedRoute from "components/GuardedRoute/GuardedRoute";
import { useIsDarkMode } from "hooks/useIsDarkMode";
import { useResizeObserverDevErrorFix } from "hooks/useResizeObserverDevErrorFix";
import { useSelectedAppThemeFromSettings } from "hooks/useSelectedAppThemeFromSettings";
import { ROUTES, sizes } from "library/constants";
import { AdminPage } from "pages/AdminPage/AdminPage";
import { CompanyBenefitImplementationPage } from "pages/CompanyBenefitImplementationPage/CompanyBenefitImplementationPage";
import { CompanyPackagesPage } from "pages/CompanyPackagesPage/CompanyPackagesPage";
import { CompanyProfilePage } from "pages/CompanyProfilePage/CompanyProfilePage";
import { CountryPage } from "pages/CountryPage/CountryPage";
import { DemoPage } from "pages/DemoPage/DemoPage";
import HrNeedsAttentionPage from "pages/HrNeedsAttentionPage/HrNeedsAttentionPage";
import { HrPackageEmployeesPage } from "pages/HrPackageEmployeesPage/HrPackageEmployeesPage";
import HrPackagesPage from "pages/HrPackagesPage/HrPackagesPage";
import { HrPage } from "pages/HrPage/HrPage";
import { UnauthorizedPage } from "pages/UnauthorizedPage/UnauthorizedPage";
import { useGetAuthorization } from "state/queries/useGetAuthorization";
import { useGetTaxonomies } from "state/queries/useGetTaxonomies";

import { useMemo } from "react";
import grecoTriangleBg from "./assets/GrECoGraphicTriangles.svg";

function App() {
  useResizeObserverDevErrorFix();
  const { data: authorization } = useGetAuthorization();

  const location = window.location;
  const is403Page = location.pathname === ROUTES.UNAUTHORIZED_PAGE;
  const { data: taxonomies } = useGetTaxonomies(!is403Page);

  const panelIsOpen = useAtomValue(panelIsOpenState);

  const darkMode = useIsDarkMode();

  const selectedTheme = useSelectedAppThemeFromSettings();

  const fluentTheme = useMemo(() => {
    const fluentTheme = darkMode ? webDarkTheme : webLightTheme;

    const fluentThemeUpdated = {
      ...fluentTheme,

      ...fluentTheme,
      colorBrandBackgroundHover: selectedTheme.palette.purpleDark,
      colorCompoundBrandBackgroundHover: selectedTheme.palette.purpleDark,
      colorNeutralForeground2BrandHover: selectedTheme.palette.purpleDark,
      colorNeutralForeground2BrandSelected: selectedTheme.palette.purpleDark,
      colorNeutralForeground3BrandHover: selectedTheme.palette.purpleDark,
      colorNeutralForeground3BrandSelected: selectedTheme.palette.purpleDark,
      colorCompoundBrandForeground1: selectedTheme.palette.purpleDark,
      colorBrandForeground1: selectedTheme.palette.purpleDark,
      colorBrandForegroundOnLight: selectedTheme.palette.purpleDark,
      colorBrandBackground: selectedTheme.palette.purpleDark,
      colorCompoundBrandBackground: selectedTheme.palette.purpleDark,
      colorBrandBackgroundStatic: selectedTheme.palette.purpleDark,
      colorNeutralStrokeAccessibleSelected: selectedTheme.palette.purpleDark,
      colorBrandStroke1: selectedTheme.palette.purpleDark,
      colorBrandStroke2Pressed: selectedTheme.palette.purpleDark,
      colorCompoundBrandStroke: selectedTheme.palette.purpleDark,
      colorCompoundBrandStrokeHover: selectedTheme.palette.purpleDark,
    };
    return fluentThemeUpdated;
  }, [darkMode, selectedTheme]);

  if (!authorization?.roles) {
    return null;
  }

  return (
    <div className="App">
      <FluentProvider theme={fluentTheme}>
        <StyledThemeProvider theme={selectedTheme}>
          {!taxonomies && !is403Page && <AppSpinner />}
          <BrowserRouter>
            <GlsEditor>
              <AppHeader />
              <Container $isPanelOpen={panelIsOpen}>
                <Routes>
                  <Route
                    path={ROUTES.ADMIN}
                    element={
                      <GuardedRoute
                        path={ROUTES.ADMIN}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <AdminPage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.COUNTRY}
                    element={
                      <GuardedRoute
                        path={ROUTES.COUNTRY}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <CountryPage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.COMPANY_PROFILE}
                    element={
                      <GuardedRoute
                        path={ROUTES.COMPANY_PROFILE}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <CompanyProfilePage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={`${ROUTES.COMPANY_PROFILE}/:categoryId/:countryCode/:clientInternalNumberGOS`}
                    element={
                      <GuardedRoute
                        path={ROUTES.COMPANY_PROFILE}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <CompanyBenefitImplementationPage />
                      </GuardedRoute>
                    }
                  />

                  <Route
                    path={`${ROUTES.COMPANY_PROFILE}/:categoryId/:implementationId/:countryCode/:clientInternalNumberGOS`}
                    element={
                      <GuardedRoute
                        path={ROUTES.COMPANY_PROFILE}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <CompanyPackagesPage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={ROUTES.HR}
                    element={
                      <GuardedRoute
                        path={ROUTES.HR}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <HrPage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={`${ROUTES.NEEDS_ATTENTION}`}
                    element={
                      <GuardedRoute
                        path={ROUTES.HR}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <HrNeedsAttentionPage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={`${ROUTES.HR}/employees/:packageId/:categoryId/:implementationId/:countryCode/:clientInternalNumberGOS`}
                    element={
                      <GuardedRoute
                        path={ROUTES.HR}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <HrPackageEmployeesPage />
                      </GuardedRoute>
                    }
                  />
                  <Route
                    path={`${ROUTES.HR}/packages/:categoryId/:implementationId/:countryCode/:clientInternalNumberGOS`}
                    element={
                      <GuardedRoute
                        path={ROUTES.HR}
                        redirectRoute={ROUTES.UNAUTHORIZED_PAGE}
                      >
                        <HrPackagesPage />
                      </GuardedRoute>
                    }
                  />

                  <Route
                    path={ROUTES.UNAUTHORIZED_PAGE}
                    element={<UnauthorizedPage />}
                  />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route
                    path="*"
                    element={<Navigate to={ROUTES.ADMIN} replace />}
                  />
                </Routes>
              </Container>
              <ToastContainer
                style={{
                  zIndex: 9999999,
                }}
              />
            </GlsEditor>
          </BrowserRouter>
        </StyledThemeProvider>
      </FluentProvider>
    </div>
  );
}

export default App;

const Container = styled("div")<{ $isPanelOpen: boolean }>`
  position: absolute;
  overflow: auto;
  top: ${sizes.appHeader}px;
  left: 0;
  bottom: 0;
  right: 0;
  // width: ${({ $isPanelOpen }) =>
    $isPanelOpen ? "calc(100% - 340px)" : "100%"};
  width: 100%;
  background-color: ${(props) => props.theme.palette.themeBackground};

  background-image: url("${grecoTriangleBg}");
  background-size: auto;
  background-position-x: right;
  background-position-y: bottom;
  background-repeat: no-repeat;
`;
